import React, { useEffect, useRef, useState } from "react";

import { INVALID_URL_ERROR, DUPLICATE_URL_ERROR } from "../../utils/message";

import { XCrossIcon } from "../../assets";
import { globalMaxFileSize } from "../../utils/utils";

interface DocumentInfoType {
  files: { fileUrl: {}; fileName: string }[];
  urls: string[];
}
interface AddDocumentURLType {
  confirmModal: boolean;
  setConfirmModal: (value: boolean) => void;
  documentInfo: DocumentInfoType;
  setDocumentInfo: (value: any) => void;
}

// Accepted file types and maximum file size constants
const acceptedFileTypes = [
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/pdf", // .pdf
  "text/plain", // .txt
];
const maxFileSize = globalMaxFileSize; // 5 MB

/**
 * AddDocumentURLModal Component
 *
 * This component allows users to upload documents or add URLs. It includes file validation,
 * drag-and-drop functionality, and URL validation. The user can upload files and add URLs,
 * and the component will handle validating and displaying the uploaded files and added URLs.
 *
 * @param confirmModal - Boolean to control the visibility of the modal.
 * @param setConfirmModal - Function to set the visibility of the modal.
 * @param documentInfo - Object containing the initial files and URLs.
 * @param setDocumentInfo - Function to update the files and URLs.
 */
const AddDocumentURLModal: React.FC<AddDocumentURLType> = (props) => {
  const { confirmModal, setConfirmModal, documentInfo, setDocumentInfo } =
    props;
  const [url, setUrl] = useState<string>("");
  const [urlError, setUrlError] = useState<string>("");
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formState, setFormState] = useState<DocumentInfoType>({
    files: [],
    urls: [],
  });

  /**
   * Handles closing the modal by setting the confirmModal state to false.
   */
  const handleClose = () => {
    setFormState(documentInfo);
    setUrl("");
    setUrlError("");
    setFileErrors([]);
    setConfirmModal(false);
  };

  /**
   * Validates the file type and size.
   * @param file - The file to validate.
   * @returns - Error message string if validation fails, otherwise an empty string.
   */
  const validateFile = (file: File) => {
    if (!acceptedFileTypes.includes(file.type)) {
      return `${file.name} is not a valid file type.`;
    }
    if (file.size > maxFileSize) {
      return `File size ${file.size / 1024 / 1024
        } MB exceeds the maximum allowed size of 5 MB.`;
    }
    return "";
  };

  /**
   * Handles file selection through the input element.
   * Validates each file and adds it to the form state if it passes validation.
   * @param event - The file input change event.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFileErrors: string[] = [];
      const existingFileNames = formState.files.map((file) => file.fileName);

      Array.from(files).forEach((file) => {
        if (existingFileNames.includes(file.name)) {
          newFileErrors.push(`${file.name} has already been uploaded.`);
          return;
        }

        const error = validateFile(file);
        if (error) {
          newFileErrors.push(error);
        } else {
          setFormState((prev: DocumentInfoType) => ({
            ...prev,
            files: [{ fileUrl: file, fileName: file.name }],
          }));
        }
      });

      setFileErrors(newFileErrors);
    }
  };

  /**
   * Handles file drop event in the drag-and-drop area.
   * Validates each file and adds it to the form state if it passes validation.
   * @param event - The drag event.
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files) {
      const newFileErrors: string[] = [];
      const existingFileNames = formState.files.map((file) => file.fileName);

      Array.from(files).forEach((file) => {
        if (existingFileNames.includes(file.name)) {
          newFileErrors.push(`${file.name} has already been uploaded.`);
          return;
        }

        const error = validateFile(file);
        if (error) {
          newFileErrors.push(error);
        } else {
          setFormState((prev: DocumentInfoType) => ({
            ...prev,
            files: [...prev.files, { fileUrl: file, fileName: file.name }],
          }));

          // const reader = new FileReader();
          // reader.onload = () => {
          //   const base64 = reader.result as string;
          //   setFormState((prev: DocumentInfoType) => ({
          //     ...prev,
          //     files: [...prev.files, { fileUrl: base64, fileName: file.name }],
          //   }));
          // };
          // reader.readAsDataURL(file);
        }
      });

      setFileErrors(newFileErrors);
    }
  };

  /**
   * Handles changes in the URL input field.
   * Clears any previous URL validation errors.
   * @param event - The input change event.
   */
  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    setUrlError("");
  };

  /**
   * Adds the entered URL to the form state if it passes validation.
   * Valid URLs must start with http:// or https://.
   */
  const handleAddUrl = () => {
    // Validate proper URL format (must start with https://)
    const urlPattern =
      /^https:\/\/[^\s/$.?#].[^\s]*$/i;
    //const existing = formState.files.map((file) => file.fileName);
    if (formState.urls.includes(url)) {
      setUrlError(DUPLICATE_URL_ERROR);
      return;
    }
    if (urlPattern.test(url)) {
      setFormState((prev: DocumentInfoType) => ({
        ...prev,
        urls: [...prev.urls, url],
      }));
      setUrl("");
      setUrlError("");
    } else {
      setUrlError(INVALID_URL_ERROR);
    }
  };

  /**
   * Removes a URL from the form state.
   * @param removeUrl - The URL to remove.
   */
  const handleRemoveUrl = (removeUrl: string) => {
    setFormState((prev: DocumentInfoType) => ({
      ...prev,
      urls: prev.urls.filter((u) => u !== removeUrl),
    }));
  };

  /**
   * Removes a file from the form state.
   * @param fileName - The name of the file to remove.
   */
  const handleRemoveFile = (fileName: string) => {
    setFormState((prev: DocumentInfoType) => ({
      ...prev,
      files: prev.files.filter((file) => file.fileName !== fileName),
    }));
  };

  /**
   * Saves the form state to the parent component and closes the modal.
   */
  const handleSave = () => {
    setDocumentInfo((prev: DocumentInfoType) => ({
      ...prev,
      files: formState.files,
      urls: formState.urls,
    }));
    handleClose();
  };

  // Effect to set the initial form state when the modal opens
  useEffect(() => {
    if (confirmModal) {
      setFormState(documentInfo);
    }
  }, [confirmModal]);

  return (
    <>
      <div className="modal modal--hazmatDetail modal--open">
        <div className="modal__dialog">
          <div className="modal__content modal__content--lg">
            <div className="modal__header">
              <h4 className="modal__header--title">Add Document & URL</h4>
              <button
                className="btn btn__transparent btnClose"
                onClick={handleClose}
              >
                <XCrossIcon />
              </button>
            </div>
            <div className="modal__body">
              <div
                className="formRow"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">
                      Select files or drop files here
                    </label>
                    <label
                      htmlFor="UploadFile"
                      className="form__browsefile form__browsefile--orderUrl"
                    >
                      <input
                        type="file"
                        id="UploadFile"
                        className="form__uploadfiled"
                        //multiple
                        onChange={handleFileChange}
                        ref={fileInputRef}
                      />

                      <span className="btn btn__primary">Browse File</span>
                      {formState.files?.length === 0 && (
                        <span className="form__browsefile__url">
                          No file chosen
                        </span>
                      )}
                    </label>
                    {fileErrors.length > 0 && (
                      <div className="form__error">
                        {fileErrors.map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  {formState.files?.length > 0 && (
                    <div className="browsefile__container">
                      {formState.files?.map((file) => (
                        <div className="browsefile__container__files">
                          <div key={file.fileName} className="file__item">
                            {file.fileName}
                          </div>
                          <button
                            className="btn btn__danger"
                            onClick={() => handleRemoveFile(file.fileName)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="formRow">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="AttachURL" className="form__label">
                      Attach URL
                    </label>
                    <div className="form__attachUrl">
                      <div className="flexRow" style={{ alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <input
                            id="AttachURL"
                            type="text"
                            className="form__input form__input--sm"
                            placeholder="Enter URL"
                            value={url}
                            onChange={handleUrlChange}
                          />
                          {urlError && (
                            <div className="form__error">{urlError}</div>
                          )}
                        </div>
                        <button
                          className="btn btn__success"
                          type="button"
                          onClick={handleAddUrl}
                        >
                          Add URL
                        </button>
                      </div>
                      <div className="form__attachUrl__history">
                        <div className="form__attachUrl__history__header">
                          <span className="item">Url</span>
                        </div>
                        <div className="form__attachUrl__history__body">
                          {formState.urls?.map((url, index) => (
                            <div key={index} className="btn__group">
                              <a
                                className="url"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {url}
                              </a>
                              <button
                                className="btn btn__danger"
                                type="button"
                                onClick={() => handleRemoveUrl(url)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal__footer">
              <div className="btn__group">
                <button className="btn btn__white" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  disabled={
                    formState.files.length === 0 &&
                    formState.urls.length === 0 &&
                    documentInfo.files.length === 0 &&
                    documentInfo.urls.length === 0
                  }
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal__backdrop"></div>
      </div>
    </>
  );
};

export default AddDocumentURLModal;
